import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Typography,
  CircularProgress,
  Button,
  Box,
  TextField,
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AUTH_VERIFY_EMAIL, AUTH_RESEND_VERIFICATION } from '../config/Config';
import { clearForFreshLogin, persistAuthSession } from '../utils/authStorage';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [action, setAction] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const hasVerified = useRef(false);

  const handleShowEmailInput = () => {
    setShowEmailInput(true);
  };

  const handleCancelResend = () => {
    navigate('/get-started', { replace: true });
  };

  const handleResendEmail = async () => {
    if (!email.trim()) return;
    
    setResendLoading(true);
    try {
      const response = await api.post(AUTH_RESEND_VERIFICATION, { email: email.trim() });
      
      if (response.status === 200) {
        setStatus({ 
          type: 'success', 
          message: 'Verification email sent successfully! Please check your email and spam folder.' 
        });
        setShowEmailInput(false);
        
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    } catch (error) {
      let errorMessage = 'Failed to send verification email. Please try again.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    const verify = async () => {
      if (!token || hasVerified.current) return;
      hasVerified.current = true;

      try {
        const response = await api.get(`${AUTH_VERIFY_EMAIL}?token=${token}`);
        const data = response.data;

        if (response.status === 200) {
          if (data && data.action) {
            const { action: actionType, message, hasPendingInvites, inviteCount, firstInvite } = data;
            setAction(actionType);
            setStatus({ type: 'success', message: message || 'Email verified successfully!' });
            if (data.auto_login && data.token && data.user) {
              clearForFreshLogin();
              persistAuthSession(data.token, data.user);
            }

            switch (actionType) {
              case 'redirect_to_checkout':
                if (data.checkout_url) {
                  window.location.href = data.checkout_url;
                  return;
                }
                setTimeout(() => navigate('/login', { replace: true, state: { message: message || 'Email verified. Please log in.' } }), 2000);
                break;

              case 'redirect_to_dashboard':
                setTimeout(() => {
                  window.location.href = data.redirect || '/dashboard';
                }, 1500);
                break;

              case 'redirect_to_pricing':
                setTimeout(() => {
                  navigate('/get-started', {
                    replace: true,
                    state: {
                      message: message || 'Your subscription is inactive. Please re-register.'
                    }
                  });
                }, 2000);
                break;

              case 'redirect_to_accept_invite':
                setTimeout(() => {
                  window.location.href = data.redirect || (hasPendingInvites && firstInvite ? `/accept-invite?token=${firstInvite.token}` : '/dashboard');
                }, 1500);
                break;

              case 'redirect_to_login':
              default:
                setTimeout(() => {
                  window.location.href = data.redirect || '/dashboard';
                }, 2000);
            }
          } else {
            setStatus({ type: 'success', message: data.detail || 'Email verified successfully!' });
            setTimeout(() => {
              navigate('/login');
            }, 3000);
          }
        } else {
          let errorMessage = 'Verification failed.';
          let shouldShowResend = false;
          
          if (data.error) {
            if (data.error.includes('Invalid or expired') || 
                data.error.includes('verification token')) {
              errorMessage = 'This verification link has already been used or has expired. Please enter your email below to request a new verification email.';
              shouldShowResend = true;
            } else if (data.error.includes('missing')) {
              errorMessage = 'Verification link is incomplete. Please check your email and try again.';
            } else {
              errorMessage = data.error;
              shouldShowResend = true;
            }
          }
          
          setStatus({ type: 'error', message: errorMessage });
          if (shouldShowResend) {
            setShowEmailInput(true);
          }
        }
      } catch (error) {
        let errorMessage = 'Verification failed. Please try again.';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setStatus({ type: 'error', message: errorMessage });
        setShowEmailInput(true);
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token, navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
      {loading ? (
        <>
          <Typography variant="h5" gutterBottom>
            Verifying your email...
          </Typography>
          <CircularProgress />
        </>
      ) : (
        <>
          {status.type === 'success' ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'success.light',
              color: 'success.contrastText',
              borderRadius: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'success.main'
            }}>
              <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>✅</Box>
              <Typography sx={{ textAlign: 'center' }}>
                {status.message}
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'error.light',
              color: 'error.contrastText',
              borderRadius: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'error.main'
            }}>
              <Box component="span" sx={{ color: 'error.main', mr: 1, display: 'flex', alignItems: 'center' }}>⚠️</Box>
              <Typography sx={{ textAlign: 'center' }}>
                {status.message}
              </Typography>
            </Box>
          )}

          {status.type === 'success' && (
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
              {action === 'redirect_to_pricing' ? 'Redirecting to pricing page...' : 'Redirecting...'}
            </Typography>
          )}

          {status.type === 'error' && !showEmailInput && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleShowEmailInput}
                sx={{ mr: 2 }}
              >
                Enter Email to Resend Verification
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </Box>
          )}
          
          {status.type === 'error' && showEmailInput && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Enter your email to resend verification"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                sx={{ mb: 2 }}
                disabled={resendLoading}
              />
              <Button
                variant="contained"
                onClick={handleResendEmail}
                disabled={resendLoading || !email.trim()}
                sx={{ mr: 2 }}
              >
                {resendLoading ? <CircularProgress size={20} /> : 'Send'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelResend}
                disabled={resendLoading}
              >
                Cancel
              </Button>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default VerifyEmail;
